import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, BehaviorSubject, of, firstValueFrom, Subject } from 'rxjs';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from './navigation.data';
import { IMenu } from '@shared/models/menu';
import { IBreadcrumb } from '@shared/components/header-text/header-text.model';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    navegacionMenu = [];
    inProcess = false;
    private _menuParsedSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private _menuLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _navigation: BehaviorSubject<Navigation> = new BehaviorSubject<Navigation>({
        compact: [],
        default: [],
        futuristic: [],
        horizontal: []
    });
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        // Inicializar la navegación con los menús predeterminados
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter para la navegación
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Métodos públicos
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter para los datos del menú de navegación
     */
    set _navigationMenuData(menuData) {
        this.navegacionMenu = this.parsearMenuNavegacion(menuData);
        // Actualizar la navegación con los nuevos datos del menú
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    /**
     * Getter para los datos del menú de navegación
     */
    get _navigationMenuData() {
        return this.navegacionMenu;
    }

    /**
     * Esta función se utiliza para parsear los datos del menú de navegación
     * que devuelve el servicio backend.
     * @param menu 
     * @returns 
     */
    parsearMenuNavegacion(menu: IMenu[]) {
        let menuData = [];
        let hijos = []
        menu.forEach(i => {
            if (i.hijos.length > 0) {
                hijos = this.tieneHijos(i.hijos);
            }

            let item = {
                id: i.item.descripcion.toLowerCase().replace(' ', ''),
                iditem: i.item.iditem,
                title: i.item.descripcion,
                type: i.hijos.length > 0 ? 'collapsable' : 'basic',
                link: i.hijos.length == 0 ? '/' : i.item.ruta,
                exactMatch: i.hijos.length === 0,
                icon: i.item.icono ? i.item.icono : null,
                children: hijos
            }
            menuData.push(item);
        })
        this._menuParsedSubject.next(menuData);
        return menuData;
    }

    /**
    * Función recursiva que recorre los submenús para luego pintarlos.
    * @param hijos 
    */
    tieneHijos(hijos) {
        var data = [];
        let children = [];

        hijos.forEach(subItem => {
            if (subItem.hijos.length > 0) {
                let h = this.tieneHijos(subItem.hijos);
                children.push(h);
            }

            let item = {
                id: subItem.item.descripcion.toLowerCase().replace(' ', ''),
                iditem: subItem.item.iditem,
                title: subItem.item.descripcion,
                type: subItem.hijos.length > 0 ? 'collapsable' : 'basic',
                link: subItem.item.ruta,
                exactMatch: subItem.hijos.length === 0,
                icon: subItem.item.icono ? subItem.item.icono : null,
                children: children
            }

            data.push(item);
        });

        return data;
    }

    /**
     * Obtener los menús
     */
    async getMenus(force: boolean = false) {
        
        if (!this.inProcess && (this._navigationMenuData.length === 0 || force)) {
            try {
                const res = await this.getMenusX();
                this.inProcess = false;
                this._menuLoadedSubject.next(res);
            } catch (error) {
                console.error('NavigationService: Error al cargar el menú:', error);
                this.inProcess = false;
                this._menuLoadedSubject.next(false);
            }
        } 
    }

    async getMenusX(): Promise<boolean> {
        this.inProcess = true;

        const res = await firstValueFrom(this._httpClient.post(`${environment.menu}`, null));
        this._navigationMenuData = res['menu'];

        return true;
    }

    /**
     * Obtener todos los datos de navegación
     */
    get(): Observable<Navigation> {
        const navigationData = {
            compact: this._compactNavigation,
            default: this._navigationMenuData,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        }

        return of(navigationData);
    }
    
    get menuLoaded$(): Observable<boolean> {
        return this._menuLoadedSubject.asObservable();
    }

    get menuParsed$(): Observable<any[]> {
        return this._menuParsedSubject.asObservable();
    }

    // Metodo para obtener un cierto arreglo del item de menu
    getItemById(id: string): any {
        const items = this._menuParsedSubject.getValue();

        const buscar = (items: any[]): any => {
            for (const item of items) {
                if (item.id === id) {
                    return item;
                }

                if (item.children?.length > 0) {
                    const encontrado = buscar(item.children);
                    if (encontrado) {
                        return encontrado;
                    }
                }
            }

            return null;
        };

        return buscar(items);
    }

    getItemByRoute(route: string): any {
        const items = this._menuParsedSubject.getValue();

        const buscar = (items: any[]): any => {
            for (const item of items) {

                if (item.link === route) {
                    return item;
                }

                if (item.children?.length) {
                    const encontrado = buscar(item.children);
                    if (encontrado) {
                        return encontrado;
                    }
                }
            }
            return null;
        };

        return buscar(items);
    }

    /**
     * Obtiene la lista ordenada de breadcrumbs (ancestro -> nodo) para una ruta dada
     * según la jerarquía del menú cargado.
     */
    getBreadcrumbsByRoute(route: string): IBreadcrumb[] {
        const items = this._menuParsedSubject.getValue()?.length ? this._menuParsedSubject.getValue() : this.navegacionMenu;
        if (!items || !items.length) {
            return [];
        }

        const cleanRoute = (route || '').split('?')[0];

        const buscarJerarquia = (list: any[], path: any[] = []): any[] | null => {
            for (const item of list) {
                const currentPath = [...path, item];
                if (item.link && (item.link === cleanRoute || item.link === route)) {
                    return currentPath;
                }
                if (item.children && item.children.length > 0) {
                    const found = buscarJerarquia(item.children, currentPath);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
        };

        const hierarchy = buscarJerarquia(items);
        if (!hierarchy || !hierarchy.length) {
            return [];
        }

        return hierarchy.map((item, index) => {
            const isLast = index === hierarchy.length - 1;
            return {
                text: item.title,
                icon: item.icon ? item.icon : undefined,
                url: (!isLast && item.link && item.link !== '/' && item.type !== 'collapsable') ? item.link : undefined
            };
        });
    }

    hasItemByItemId(iditem: number): boolean {
        const items = this._menuParsedSubject.getValue() || [];

        const buscar = (elements: any[]): boolean => {
            for (const item of elements) {
                if (item.iditem === iditem) {
                    return true;
                }
                if (item.children?.length > 0) {
                    if (buscar(item.children)) {
                        return true;
                    }
                }
            }
            return false;
        };

        return buscar(items);
    }

    getEstadoMicroservicios() {
        let subject = new Subject<any>();

        this._httpClient.get<Response[]>(`${environment.estado_microservicios}`)
            .subscribe((resp: any) => {
                subject.next(resp.result);
            });

        return subject.asObservable();
    }
}
